import { useState, useCallback, useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { DiceState, TabletopItemType } from '@/components/tabletop/types';
import TabletopItem from '@/components/tabletop/components/TabletopItem';

interface DiceProps {
  dice: DiceState;
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onSelect?: (id: string, type: TabletopItemType, event: ReactMouseEvent) => void;
  onRoll?: (id: string) => void;
  isSelected?: boolean;
  className?: string;
}

// Default dice size
const DEFAULT_DICE_SIZE = 50;

export default function Dice({
  dice,
  onDragStart,
  onSelect,
  onRoll,
  isSelected = false,
  className = ''
}: DiceProps) {
  const { id, position, rotation, value, sides, color, size, zIndex, rolling, rollId } = dice;
  
  // State to store the animated face value
  const [animatedValue, setAnimatedValue] = useState(value);
  // Ref for interval to clean up properly
  const rollAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Animation effect for rolling dice
  useEffect(() => {
    // If dice is rolling, start animation
    if (rolling || (rollId ?? 0) > 0) {
      // Clear any existing interval
      if (rollAnimationRef.current) {
        clearInterval(rollAnimationRef.current);
      }
      
      // Create an interval to update dice face rapidly
      rollAnimationRef.current = setInterval(() => {
        // Show random face
        setAnimatedValue(Math.floor(Math.random() * sides) + 1);
      }, 85); // Update quickly while the die tumbles

      const settleTimeout = setTimeout(() => {
        if (rollAnimationRef.current) {
          clearInterval(rollAnimationRef.current);
          rollAnimationRef.current = null;
        }
        setAnimatedValue(value);
      }, 700);

      return () => {
        clearTimeout(settleTimeout);
        if (rollAnimationRef.current) {
          clearInterval(rollAnimationRef.current);
        }
      };
    } else {
      // When not rolling, clear the interval
      if (rollAnimationRef.current) {
        clearInterval(rollAnimationRef.current);
        rollAnimationRef.current = null;
      }
      
      // Update to the actual value when rolling stops
      setAnimatedValue(value);
    }
    
    // Cleanup on unmount
    return () => {
      if (rollAnimationRef.current) {
        clearInterval(rollAnimationRef.current);
      }
    };
  }, [rolling, rollId, sides, value]);
  
  // Handle right click to roll the dice
  const handleRightClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRoll) onRoll(id);
  }, [id, onRoll]);
  
  // Double click also rolls
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRoll) onRoll(id);
  }, [id, onRoll]);
  
  // Map color string to actual Tailwind classes
  const getColorClasses = (colorName: string) => {
    const colorMap: Record<string, {bg: string, border: string, pip: string}> = {
      red: {bg: 'bg-red-200', border: 'border-red-500', pip: 'bg-red-800'},
      blue: {bg: 'bg-blue-200', border: 'border-blue-500', pip: 'bg-blue-800'},
      green: {bg: 'bg-green-200', border: 'border-green-500', pip: 'bg-green-800'},
      yellow: {bg: 'bg-yellow-200', border: 'border-yellow-500', pip: 'bg-yellow-800'},
      purple: {bg: 'bg-purple-200', border: 'border-purple-500', pip: 'bg-purple-800'},
      pink: {bg: 'bg-pink-200', border: 'border-pink-500', pip: 'bg-pink-800'},
      indigo: {bg: 'bg-indigo-200', border: 'border-indigo-500', pip: 'bg-indigo-800'},
      gray: {bg: 'bg-gray-200', border: 'border-gray-500', pip: 'bg-gray-800'},
      // Default to gray if color is not found
      default: {bg: 'bg-gray-200', border: 'border-gray-500', pip: 'bg-gray-800'}
    };
    
    return colorMap[colorName] || colorMap.default;
  };
  
  const colorClasses = getColorClasses(color);
  
  // Handle select with fallback for when onSelect is undefined
  const handleItemSelect = useCallback((id: string, type: TabletopItemType, event: ReactMouseEvent) => {
    if (onSelect) {
      onSelect(id, type, event);
    }
  }, [onSelect]);
  
  // Function to render pips with correct color class
  const getDicePips = (value: number) => {
    const pipClass = `${colorClasses.pip} rounded-full w-3 h-3`;
    
    switch (value) {
      case 1:
        return (
          <div className="flex items-center justify-center w-full h-full">
            <div className={pipClass}></div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-2 gap-2 w-full h-full p-2">
            <div className={`${pipClass} justify-self-start self-start`}></div>
            <div className={`${pipClass} justify-self-end self-end`}></div>
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-3 gap-1 w-full h-full p-2">
            <div className={`${pipClass} justify-self-start self-start`}></div>
            <div className={`${pipClass} col-start-2 row-start-2 justify-self-center self-center`}></div>
            <div className={`${pipClass} col-start-3 row-start-3 justify-self-end self-end`}></div>
          </div>
        );
      case 4:
        return (
          <div className="grid grid-cols-2 gap-2 w-full h-full p-2">
            <div className={`${pipClass} justify-self-start self-start`}></div>
            <div className={`${pipClass} justify-self-end self-start`}></div>
            <div className={`${pipClass} justify-self-start self-end`}></div>
            <div className={`${pipClass} justify-self-end self-end`}></div>
          </div>
        );
      case 5:
        return (
          <div className="grid grid-cols-3 grid-rows-3 gap-1 w-full h-full p-2">
            <div className={`${pipClass} col-start-1 row-start-1 justify-self-start self-start`}></div>
            <div className={`${pipClass} col-start-3 row-start-1 justify-self-end self-start`}></div>
            <div className={`${pipClass} col-start-2 row-start-2 justify-self-center self-center`}></div>
            <div className={`${pipClass} col-start-1 row-start-3 justify-self-start self-end`}></div>
            <div className={`${pipClass} col-start-3 row-start-3 justify-self-end self-end`}></div>
          </div>
        );
      case 6:
        return (
          <div className="grid grid-cols-2 grid-rows-3 gap-2 w-full h-full p-2">
            <div className={`${pipClass} justify-self-start self-start`}></div>
            <div className={`${pipClass} justify-self-end self-start`}></div>
            <div className={`${pipClass} justify-self-start self-center`}></div>
            <div className={`${pipClass} justify-self-end self-center`}></div>
            <div className={`${pipClass} justify-self-start self-end`}></div>
            <div className={`${pipClass} justify-self-end self-end`}></div>
          </div>
        );
      default:
        // For dice with more than 6 sides, just show the number
        return (
          <div className="flex items-center justify-center w-full h-full">
            <span className="text-lg font-bold">{value}</span>
          </div>
        );
    }
  };
  
  return (
    <TabletopItem
      id={id}
      type="dice"
      position={position}
      rotation={rotation}
      width={size || DEFAULT_DICE_SIZE}
      height={size || DEFAULT_DICE_SIZE}
      isSelected={isSelected}
      onSelect={handleItemSelect}
      onDragStart={onDragStart}
      className={className}
      zIndex={zIndex}
    >
      <div 
        key={rollId ?? 0}
        className={`dice-face relative h-full w-full ${colorClasses.bg} ${colorClasses.border} rounded-2xl border-2 shadow-[0_12px_24px_rgba(15,23,42,0.18)] ${(rollId ?? 0) > 0 ? 'dice-face-rolling' : ''}`}
        onContextMenu={handleRightClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/45 via-transparent to-black/6" />
        {/* Show animated value instead of spinning animation */}
        {getDicePips(animatedValue)}
        
        {/* For dice with more than 6 sides, show the number of sides */}
        {sides > 6 && (
          <div className="absolute bottom-0 right-0 rounded-tl-lg bg-black/35 px-1.5 py-0.5 text-xs text-white">
            d{sides}
          </div>
        )}
      </div>
    </TabletopItem>
  );
} 
